import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import {
  BuilderContext,
  formatHours,
  formatTime,
  shortWeekday,
  text,
} from "../ai-weekly-template-builder.utils";

interface Props {
  context: BuilderContext;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

/**
 * Everything the scheduling system knows about this authorization, shown before
 * the coordinator spends a generation.
 *
 * The weekday occurrence counts are the important part: they are the real
 * calendar, computed once and used identically by this panel, the prompt and
 * the verifier.
 *
 * It is a lot of detail, and it stops being the point once a result exists — so
 * it collapses to a one-line summary that the caller can reopen.
 */
export default function AiWeeklyTemplateContextPanel(props: Props) {
  const { authorization, periods, totals, warnings } = props.context;

  const summary = [
    authorization.periodType,
    `${text(authorization.startDate)} → ${text(authorization.endDate)}`,
    authorization.periodMinutes === null
      ? null
      : `${formatHours(authorization.periodMinutes)} per period`,
    `${formatHours(totals.effectiveAuthorizedMinutes)} authorized`,
    `${periods.length} period${periods.length === 1 ? "" : "s"}`,
  ]
    .filter((part) => part !== null)
    .join("  ·  ");

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      <Flex alignItems="center" gap={3} justifyContent="space-between">
        <Box minW={0}>
          <Heading size="xs">Authorization details</Heading>
          {!props.isExpanded && (
            <Text color="gray.600" fontSize="sm" noOfLines={1}>
              {summary}
            </Text>
          )}
        </Box>
        <Button flexShrink={0} size="xs" variant="outline" onClick={props.onToggleExpanded}>
          {props.isExpanded ? "Hide" : "Show"}
        </Button>
      </Flex>

      {/* Warnings are the one thing worth surfacing even when collapsed —
          "this authorization already ended" changes what you do next. */}
      {!props.isExpanded && warnings.length > 0 && (
        <Text color="orange.600" fontSize="sm">
          {warnings.length} note{warnings.length === 1 ? "" : "s"} about this authorization — open
          the details to read {warnings.length === 1 ? "it" : "them"}.
        </Text>
      )}

      {props.isExpanded && <ExpandedDetails context={props.context} />}
    </Box>
  );
}

function ExpandedDetails({ context }: { context: BuilderContext }) {
  const { authorization, periods, totals, existingTemplateShifts, warnings } = context;

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      {warnings.map((warning) => (
        <Alert key={warning.code} status="warning">
          <AlertIcon />
          <AlertDescription>{warning.message}</AlertDescription>
        </Alert>
      ))}

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
        <Field label="Period type" value={authorization.periodType} />
        <Field
          label="Date range"
          value={`${text(authorization.startDate)} → ${text(authorization.endDate)}`}
        />
        <Field
          label="Per period"
          value={
            authorization.periodMinutes === null ? "—" : formatHours(authorization.periodMinutes)
          }
        />
        <Field
          label="Maximum"
          value={authorization.maxMinutes === null ? "None" : formatHours(authorization.maxMinutes)}
        />
        <Field label="Contract" value={authorization.contractTypeName} />
        <Field label="Service code" value={authorization.serviceCode} />
        <Field label="Total authorized" value={formatHours(totals.effectiveAuthorizedMinutes)} />
        <Field label="Already scheduled" value={formatHours(totals.alreadyScheduledMinutes)} />
      </SimpleGrid>

      {authorization.dailyMinutesByWeekday !== null && (
        <Box>
          <Heading size="xs">Daily limits by weekday</Heading>
          <Text fontSize="sm">
            {authorization.dailyMinutesByWeekday
              .map((entry) => `${shortWeekday(entry.dayOfWeek)} ${formatHours(entry.minutes)}`)
              .join("  ·  ")}
          </Text>
        </Box>
      )}

      {authorization.weeklyDayRestrictions !== null && (
        <Box>
          <Heading size="xs">Weekly day restrictions</Heading>
          <Text fontSize="sm">
            {authorization.weeklyDayRestrictions
              .map(
                (restriction) =>
                  `up to ${restriction.numDays} day(s) at ${formatHours(
                    restriction.maxMinutesPerDay
                  )}`
              )
              .join("  ·  ")}
          </Text>
        </Box>
      )}

      <Box>
        <Heading pb={1} size="xs">
          Current weekly template
        </Heading>
        {existingTemplateShifts.length === 0 ? (
          <Text color="gray.500" fontSize="sm">
            None yet.
          </Text>
        ) : (
          <Text fontSize="sm">
            {existingTemplateShifts
              .map(
                (shift) =>
                  `${shortWeekday(shift.dayOfWeek)} ${formatTime(shift.startTime)}-${formatTime(
                    shift.endTime
                  )}`
              )
              .join("  ·  ")}
          </Text>
        )}
      </Box>

      <Box overflowX="auto">
        <Heading pb={1} size="xs">
          Authorization periods ({periods.length})
        </Heading>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Period</Th>
              <Th>Schedulable window</Th>
              <Th isNumeric>Limit</Th>
              <Th isNumeric>Realistic</Th>
              <Th isNumeric>Already scheduled</Th>
              <Th>Weekday occurrences</Th>
            </Tr>
          </Thead>
          <Tbody>
            {periods.map((period) => (
              <Tr key={period.periodIndex}>
                <Td>{period.label}</Td>
                <Td>
                  {text(period.effectiveStart)} → {text(period.effectiveEnd)}
                  {period.isPartialPeriod && (
                    <Text as="span" color="orange.500" fontSize="xs">
                      {" "}
                      (partial, {period.effectiveDays}/{period.daysInPeriod} days)
                    </Text>
                  )}
                </Td>
                <Td isNumeric>{formatHours(period.availableMinutes)}</Td>
                <Td isNumeric>
                  {period.isPartialPeriod ? formatHours(period.proratedAvailableMinutes) : "—"}
                </Td>
                <Td isNumeric>{formatHours(period.alreadyScheduledMinutes)}</Td>
                <Td fontSize="xs">
                  {period.weekdayOccurrences
                    .map(
                      (occurrence) => `${shortWeekday(occurrence.dayOfWeek)}×${occurrence.count}`
                    )
                    .join(" ")}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {periods.some((period) => period.isPartialPeriod) && (
          <Alert mt={2} status="info">
            <AlertIcon />
            <Box>
              <AlertTitle fontSize="sm">Partial periods keep their full limit</AlertTitle>
              <AlertDescription fontSize="sm">
                A payer that authorizes a monthly amount still allows the whole amount in a month
                the authorization starts partway through. The &quot;realistic&quot; column is what
                can actually be filled in the days available.
              </AlertDescription>
            </Box>
          </Alert>
        )}
      </Box>
    </Box>
  );
}

function Field(props: { label: string; value: string }) {
  return (
    <Box>
      <Text color="gray.500" fontSize="xs" textTransform="uppercase">
        {props.label}
      </Text>
      <Text fontSize="sm" fontWeight="semibold">
        {props.value}
      </Text>
    </Box>
  );
}
